import { getSignalRHubConnection } from './signalR';
import { User } from '@vooban/tc3-types/common';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { BotInfo, BrowserInfo, detect, NodeInfo } from 'detect-browser';
import { createDndContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import {
  addErrorHandler,
  AppError,
  BOOTSTRAPPING,
  getAppStatus,
  LOADING_SOURCE_CODE,
  LOAD_ERROR,
  MOUNTING,
  NOT_BOOTSTRAPPED,
  NOT_LOADED,
  NOT_MOUNTED,
  registerApplication,
  SKIP_BECAUSE_BROKEN,
  start,
  UPDATING,
} from 'single-spa';
import { isProcessEnvFalse, isProcessEnvTrue, setupGlobals } from '@vooban/tc3-frontend-globals';
import Auth from './auth/auth';
import { getDatadogLogsConfiguration, getDatadogRumConfiguration } from './datadogConfig';
import { importMfe } from './systemJsDiagnostics';
import { injectSpritesheet } from './uiHelpers';

const auth = new Auth();

const singleSpaUnavailableStatus = [BOOTSTRAPPING, MOUNTING, UPDATING, LOAD_ERROR];

const singleSpaFatalStatus = [NOT_MOUNTED, NOT_LOADED, NOT_BOOTSTRAPPED, SKIP_BECAUSE_BROKEN];

const singleSpaNotFoundStatus = [LOADING_SOURCE_CODE];

declare global {
  interface Window {
    dataLayer: any[];
    System: { import(id: string): Promise<any> };
    SystemJS: Window['System'];
  }
}

window.dataLayer = window.dataLayer || [];
// Legacy global still used by MFE parcels (System.import calls in other repos). Set here, not in an inline
// <script>: script-src has no 'unsafe-inline' (TC3-17078).
window.SystemJS = window.System;

const logout = (returnToCurrentUrl = false) => {
  if (document.hidden) return;

  const currentUrl = window.location.href;
  datadogLogs.logger.warn(`Logout was called from ${currentUrl}`);
  if (returnToCurrentUrl) localStorage.setItem('beforeLogoutURL', currentUrl);
  auth.logout();
};

setupGlobals();

// The abstraction seam every MFE consumes (via the axios refresh interceptor and
// SignalR). Now a thin pass-through to the SDK, which handles on-demand refresh,
// single-flight de-duplication, and cross-tab serialization internally.
window.qsl.tc3.auth.getTokenSilently = () => auth.getTokenSilently();

const isGoogleAnalyticsActive = () => !!process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID;

const shouldDisplayBanner = () => {
  const displayBanner = isProcessEnvTrue('REACT_APP_SHOULD_DISPLAY_BANNER');
  return displayBanner;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function gtag(..._params: any[]) {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

const appendUserIdGoogleAnalyticsTag = (user: User | null) => {
  if (user) {
    gtag('config', process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID, {
      userId: user.sub,
      sessionId: user.sid,
      custom_map: { dimension1: 'userId', dimension2: 'sessionId' },
    });
  }
};

const appendGoogleAnalyticsTags = () => {
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GOOGLE_ANALYTICS_TRACKING_ID}`;
  script.async = true;
  document.head.appendChild(script);

  gtag('js', new Date());
};

isGoogleAnalyticsActive() && appendGoogleAnalyticsTags();

const initializeDatadogLogs = () => {
  const config = getDatadogLogsConfiguration();
  if (config) {
    datadogLogs.init(config);
  }
};

const initializeDatadogRum = () => {
  const config = getDatadogRumConfiguration();
  if (config) {
    datadogRum.init(config);
  }
};

interface IApp {
  paths: string[];
  entrypoint: string;
}

const tc3OperationalSites = 'operationalSites';
const tc3Invoicing = 'invoicing';
const tc3Flash = 'flash';
const tc3Project = 'project';
const tc3Sales = 'sales';
const tc3Appointment = 'appointment';
const tc3Vessel = 'vessel';
const tc3Inventory = 'inventory';
const tc3Telemetry = 'telemetry';
const tc3Workflow = 'workflow';
const tc3Menu = 'menu';
const tc3Errors = 'errors';

const contextualApps: { [key: string]: IApp } = {
  [tc3OperationalSites]: {
    paths: ['operational-sites'],
    entrypoint: '/tc3-operational-sites/entrypoint.min.js',
  },
  [tc3Invoicing]: { paths: ['invoices'], entrypoint: '/tc3-invoicing/entrypoint.min.js' },
  [tc3Flash]: { paths: ['flash'], entrypoint: '/tc3-flash/entrypoint.min.js' },
  [tc3Project]: {
    paths: ['projects', 'claims', 'purchase-requests', 'purchase-orders', 'purchase-invoices'],
    entrypoint: '/tc3-projects/entrypoint.min.js',
  },
  [tc3Sales]: {
    paths: ['sales', 'admin-settings', 'user-settings', 'external-links'],
    entrypoint: '/tc3-sales/entrypoint.min.js',
  },
  [tc3Appointment]: { paths: ['truck-appointments'], entrypoint: '/tc3-truck-appointments/entrypoint.min.js' },
  [tc3Vessel]: { paths: ['vessels'], entrypoint: '/tc3-vessel/entrypoint.min.js' },
  [tc3Inventory]: { paths: ['client-inventories'], entrypoint: '/tc3-inventory/entrypoint.min.js' },
  [tc3Telemetry]: { paths: ['telemetry'], entrypoint: '/tc3-telemetry/entrypoint.min.js' },
  [tc3Workflow]: { paths: ['workflow'], entrypoint: '/tc3-workflow/entrypoint.min.js' },
};

const tc3AppNames = Object.keys(contextualApps);

const dndContext = createDndContext(HTML5Backend);

function init() {
  initializeDatadogLogs();
  initializeDatadogRum();

  const hubConnection = getSignalRHubConnection();

  registerApplication(
    tc3Menu,
    () => importMfe('/tc3-menu/entrypoint.min.js'),
    () => true, //Always visible
    { getUser: Auth.getUser, hubConnection, dndContext }
  );

  tc3AppNames.forEach(appName => {
    const { entrypoint } = contextualApps[appName];
    registerApplication(
      appName,
      () => importMfe(entrypoint),
      () => handleApplicationActiveState(appName),
      {
        getUser: Auth.getUser,
        hubConnection,
        dndContext,
      }
    );
  });

  registerApplication(
    'not-found',
    () => import('./notFoundPage'),
    () => window.location.pathname === '/404'
  );
  registerApplication(
    'unavailable',
    () => import('./unavailablePage'),
    () => window.location.pathname === '/503'
  );
  registerApplication(
    'internal-error',
    () => import('./internalErrorPage'),
    () => window.location.pathname === '/500'
  );

  addErrorHandler((err: AppError) => {
    console.log(err);
    if (err.appOrParcelName === tc3Menu) return;

    const status = getAppStatus(err.appOrParcelName) || '';
    console.warn(`${err.appOrParcelName} app has status ${status}`);
    if (singleSpaNotFoundStatus.includes(status)) return window.location.replace('/404');
    if (singleSpaFatalStatus.includes(status)) return window.location.replace('/500');
    if (singleSpaUnavailableStatus.includes(status)) return window.location.replace('/503');
  });

  start();

  if (shouldDisplayBanner()) {
    const header = document.getElementById('header-env');
    if (header !== null) {
      header.style.display = 'flex';
      header.textContent = process.env.REACT_APP_BANNER_VALUE || '';
    }
  }

  if (Auth.isLoggedIn()) {
    isGoogleAnalyticsActive() && appendUserIdGoogleAnalyticsTag(Auth.getUser());
  }
}

// NOTE: The implicit-flow renewal engine (renewAccessToken, scheduleRenewal,
// the worker-timers timer, the BroadcastChannel('token-synchronizer') listener,
// sessionHash polling, and the currentRenewalPromise singleton) has been removed.
// @auth0/auth0-spa-js now owns the token lifecycle: getTokenSilently() refreshes
// on demand, single-flights concurrent callers, and serializes refreshes across
// tabs via the Web Locks API over its shared localStorage cache.

const locationStartsWithAppPath = (name: string) => {
  // This expression matches /{appName} or /{appname}/*
  const regex = new RegExp('^/' + name + '($|/.*)');
  return !!window.location.pathname.match(regex);
};

const isValidPath = () => {
  return (
    Object.keys(contextualApps).some(appName =>
      contextualApps[appName].paths.some(path => locationStartsWithAppPath(path))
    ) || ['/', '/404', '/403', '/503'].some(path => window.location.pathname === path)
  );
};

const handleApplicationActiveState = (appName: string) => {
  return contextualApps[appName].paths.some(path => locationStartsWithAppPath(path));
};

const callbackAction = async () => {
  try {
    // Exchanges the ?code for tokens (PKCE) and mirrors them to the legacy keys.
    await auth.handleAuthentication();
    const savedUrlToRedirect = localStorage.getItem('beforeLogoutURL') || localStorage.getItem('requestedURL');
    localStorage.removeItem('requestedURL');
    localStorage.removeItem('beforeLogoutURL');
    window.location.replace(savedUrlToRedirect || '/');
  } catch (err) {
    const message = (err as Error)?.message ?? '';
    datadogLogs.logger.error(`Could not complete login [${message}]`);
    window.location.replace(`login-error.html${message ? `?error=${encodeURIComponent(message)}` : ''}`);
  }
};

const welcomeAction = () => {
  window.location.replace('welcome.html');
};

const isBrowserSupported = (browser: BrowserInfo | BotInfo | NodeInfo) => {
  if (isProcessEnvFalse('REACT_APP_ENABLE_RESTRICT_BROWSERS')) return true;

  switch (browser.name) {
    case 'chrome':
    case 'edge-chromium':
    case 'crios':
      return true;
    default:
      return false;
  }
};

const getDefaultAppBasedOnRole = (user: User) => {
  const isInternalUser = user.isInternal && isProcessEnvTrue('REACT_APP_FEATURE_WORKFLOW');
  // user with more than a group will fallback on default
  const [role] = user.groups.length > 1 ? [] : user.groups;
  switch (role) {
    case 'inventoryreader':
      return tc3Inventory;
    case 'inventoryclerk':
      return tc3Inventory;
    case 'maritimeagent':
      return tc3Project;
    case 'gatekeeper':
      return tc3Appointment;
    default:
      return isInternalUser ? tc3Workflow : tc3Appointment;
  }
};

const getDefaultPage = () => {
  const user = Auth.getUser();
  if (!user) return '';

  const baseApp = getDefaultAppBasedOnRole(user);
  return contextualApps[baseApp].paths[0];
};

window.addEventListener('load', async () => {
  const browser = detect();

  injectSpritesheet('/static/icons.svg').catch();

  registerApplication(
    'unsupported-browser',
    () => import('./unsupportedBrowserPage'),
    () => window.location.pathname === '/unsupported-browser'
  );
  registerApplication(
    tc3Errors,
    () => import('./unauthorizedPage'),
    () => window.location.pathname === '/403'
  );

  switch (window.location.pathname) {
    case '/401':
    case '/logout':
      return logout();
    case '/callback':
      return callbackAction();
    case '/welcome':
      return welcomeAction();
    case '/unsupported-browser':
    case '/403':
      return start();
    default:
      if (browser && !isBrowserSupported(browser)) {
        window.location.replace('/unsupported-browser');
        console.log(`Browser:`, browser.name, browser.version, '| OS:', browser.os);
      } else if (!(await auth.ensureSession())) {
        // No valid session could be silently established from the stored refresh
        // token, so start the Authorization Code + PKCE login redirect.
        auth.redirectToLogin();
      } else {
        // ensureSession has just populated the legacy keys, so getUser() is current.
        const user = Auth.getUser();
        if (!user?.groups?.length) {
          window.location.replace('/403');
        } else if (!isValidPath()) {
          window.location.replace('/404');
        } else if (window.location.pathname === '/') {
          window.location.replace(getDefaultPage());
        } else {
          init();
        }
      }
      break;
  }
});

window.addEventListener('single-spa:request-login', () => {
  localStorage.setItem('requestedURL', window.location.href);
  auth.login();
});

window.addEventListener('single-spa:request-logout', () => logout(true));
